import { useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { LogInBtn } from 'components';
import { isMinor } from 'core';
import { selectIsLoggedUser } from 'store/user/userSlice';
import { useAppSelector } from 'store/hooks';
import { useGetShortSignInTnCQuery } from 'store/terms-and-conditions/termsAPI';

import { useFormFields, useServerErrors } from '@britishcouncil/react-forms';
import { MainContainer, PageContainer, PageLoader, Quote } from '@britishcouncil/react-solas-ors3';
import { BcAlert } from '@britishcouncil/react-solas';

import { DEFAULT_FORM_VALUES, SignupFormModel } from './types';
import { SignupForm, SuccessMsg, TnCModal } from './components';
import { getValidationSchema } from './validationSchema';
import { useSignupSubmit } from './useSignupSubmit';

export const Signup = () => {
  const isLoggedUser = useAppSelector(selectIsLoggedUser);

  const [showTnCModal, setTnCModal] = useState(false);
  const { data: shortTnC } = useGetShortSignInTnCQuery();

  const {
    checkEmail,
    emailChecking,
    lastCheckedEmail,
    userStatus,
    submit,
    registrationCompleted,
    parentInvitationSend,
    isSubmitting,
    serverErrors,
  } = useSignupSubmit();

  const formik = useFormik<SignupFormModel>({
    initialValues: DEFAULT_FORM_VALUES,
    validationSchema: getValidationSchema({
      withEmails: !isLoggedUser,
      withPasswords: userStatus === 'new' && !isLoggedUser,
    }),
    onSubmit: (values) => submit(values, isLoggedUser),
  });

  const fields = useFormFields(formik);
  const mainErrors = useServerErrors(formik, serverErrors);
  const { values, errors } = formik;

  const showMinorForm = useMemo(
    () => !errors.dateOfBirth && isMinor(values.dateOfBirth),
    [values.dateOfBirth, errors.dateOfBirth]
  );

  const showPasswordForm = useMemo(
    () => userStatus === 'new' && lastCheckedEmail === values.email && !showMinorForm && !isLoggedUser,

    [userStatus, showMinorForm, values.email, lastCheckedEmail, isLoggedUser]
  );

  const showSubmitBtn = useMemo(
    () => (!!values.email && !!values.confirmEmail && !errors.email && !errors.confirmEmail) || isLoggedUser,
    [values.email, values.confirmEmail, errors.email, errors.confirmEmail, isLoggedUser]
  );

  useEffect(() => {
    if (values.email && values.confirmEmail && !errors.email && !errors.confirmEmail) {
      checkEmail(values.email);
    }
  }, [values.email, values.confirmEmail, errors.email, errors.confirmEmail, checkEmail]);

  if (registrationCompleted)
    return (
      <MainContainer>
        <SuccessMsg email={parentInvitationSend ? formik.values.parentEmail : undefined} />
      </MainContainer>
    );

  return (
    <>
      <MainContainer>
        <h1>Register for an account</h1>
        <BcAlert type="note" title="Important Information">
          <p>Please complete the account owner details below.</p>
          <p>
            For candidates who are 18 years and above this should be the candidate’s details and for minors, this should
            be Parent/Guardian details.
          </p>
          <p>
            Please note that if the account details are inaccurate, this may result to inaccuracies in representing
            candidate details.
          </p>
        </BcAlert>
      </MainContainer>

      <PageContainer className="page-container">
        {isLoggedUser && (
          <Quote title="" intent="info">
            Please fill below information to create account
          </Quote>
        )}
        <SignupForm
          fields={fields}
          showMinorForm={showMinorForm && !emailChecking}
          showPasswordForm={showPasswordForm && !emailChecking}
          showEmailForm={!isLoggedUser}
          showSubmitBtn={showSubmitBtn && !emailChecking}
          isSubmitting={isSubmitting}
          tnCText={shortTnC?.text ?? ''}
          onSubmit={formik.submitForm}
          onTnCClick={() => setTnCModal(true)}
          mainErrors={mainErrors}
        />
        {emailChecking && <PageLoader />}
      </PageContainer>
      {!isLoggedUser && (
        <StyledLoginContainer>
          <p>
            Already have an account? <LogInBtn />
          </p>
        </StyledLoginContainer>
      )}
      <TnCModal show={showTnCModal} onExit={() => setTnCModal(false)} />
    </>
  );
};

const StyledLoginContainer = styled(MainContainer)`
  text-align: center;
`;
